import React, { useEffect, useState } from "react";
import { Box, Button, Chip, IconButton, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { WS_TYPES } from "../wsProtocol";
import ActivityResults from "./ActivityResults";

const CLOUD_COLORS = ["#0080dc", "#43a047", "#fb8c00", "#8e24aa", "#e53935", "#00897b", "#5c6bc0", "#ec407a", "#26a69a", "#7cb342", "#5d4037", "#00acc1"];

// Student side of a quick activity:
// - poll: pick an option, watch live bars
// - word-cloud game: words scattered like a cloud — tap the correct one (locked
//   after one tap; correct answers can earn leaderboard points)
const ActivityOverlay = ({ activity, ownVote, actAck, send }) => {
  const { t } = useTranslation();
  const [minimized, setMinimized] = useState(false);
  const [myPick, setMyPick] = useState(null);

  // reset when a new activity starts
  useEffect(() => {
    setMinimized(false);
    setMyPick(ownVote && ownVote.key ? ownVote.key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.kind, activity?.question]);

  if (!activity) return null;

  const isPoll = activity.kind === "poll";
  const answered = myPick !== null;
  const wasCorrect = actAck ? actAck.correct : ownVote ? ownVote.correct : null;

  const vote = (key) => {
    if (!isPoll && answered) return;
    setMyPick(key);
    send({ t: WS_TYPES.ACT_VOTE, answer: key });
  };

  if (minimized) {
    return (
      <Button
        variant="contained"
        color="secondary"
        onClick={() => setMinimized(false)}
        sx={{ position: "fixed", bottom: 80, insetInlineStart: 24, zIndex: 30, borderRadius: "100vh", fontWeight: 700 }}
      >
        {isPoll ? "📊" : "☁️"} {t("wb_activity_open")}
      </Button>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 30,
        width: "min(620px, 94vw)",
        maxHeight: "62vh",
        overflowY: "auto",
        borderRadius: "18px",
        padding: "16px 20px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 1 }}>
        <Typography fontWeight={700} sx={{ flex: 1 }}>
          {isPoll ? "📊" : "☁️"} {activity.question}
        </Typography>
        {activity.closed && <Chip size="small" label={t("wb_activity_closed")} />}
        <IconButton size="small" onClick={() => setMinimized(true)}>▁</IconButton>
      </Box>

      {isPoll ? (
        <>
          {!activity.closed && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: 2 }}>
              {activity.options.map((option) => (
                <Button
                  key={option.key}
                  variant={myPick === option.key ? "contained" : "outlined"}
                  onClick={() => vote(option.key)}
                  sx={{ borderRadius: "12px", justifyContent: "flex-start", fontWeight: 600 }}
                >
                  {option.title}
                </Button>
              ))}
              {answered && (
                <Typography fontSize="0.78rem" color="text.secondary">{t("wb_vote_changeable")}</Typography>
              )}
            </Box>
          )}
          {(answered || activity.closed) && (
            <ActivityResults activity={activity} votesLabel={t("wb_votes")} />
          )}
        </>
      ) : (
        <>
          {/* the cloud: scattered, tappable words */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px 14px",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px 4px 14px",
            }}
          >
            {activity.options.map((option, index) => {
              const isMine = myPick === option.key;
              const revealCorrect = activity.correctKey === option.key; // present only after close
              const size = 1 + ((index * 37) % 5) * 0.14; // deterministic varied sizes
              const tilt = (((index * 53) % 11) - 5) * 1.6; // deterministic slight rotation
              return (
                <Box
                  key={option.key}
                  component="button"
                  onClick={() => vote(option.key)}
                  disabled={answered || activity.closed}
                  sx={{
                    border: "none",
                    cursor: answered || activity.closed ? "default" : "pointer",
                    background: revealCorrect
                      ? "#c8e6c9"
                      : isMine
                        ? (wasCorrect === false ? "#ffcdd2" : "#bbdefb")
                        : "rgba(0,0,0,0.05)",
                    outline: isMine ? "3px solid #0080dc" : "none",
                    borderRadius: "999px",
                    padding: `${6 * size}px ${16 * size}px`,
                    fontSize: `${size}rem`,
                    fontWeight: 800,
                    fontFamily: "inherit",
                    color: revealCorrect ? "#1b5e20" : CLOUD_COLORS[index % CLOUD_COLORS.length],
                    transform: `rotate(${tilt}deg)`,
                    transition: "transform 150ms, background 200ms",
                    "&:hover": !answered && !activity.closed ? { transform: "scale(1.1) rotate(0deg)" } : {},
                  }}
                >
                  {revealCorrect ? "✓ " : ""}{option.title}
                </Box>
              );
            })}
          </Box>

          {answered && wasCorrect !== null && (
            <Chip
              color={wasCorrect ? "success" : "error"}
              label={
                wasCorrect
                  ? `🎉 ${t("wb_correct_pick")}${actAck && actAck.points > 0 ? ` +${actAck.points}` : ""}`
                  : t("wb_wrong_pick")
              }
              sx={{ fontWeight: 700, width: "100%" }}
            />
          )}
          {!answered && !activity.closed && (
            <Typography fontSize="0.8rem" color="text.secondary" textAlign="center">
              {t("wb_tap_correct_word")}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

export default ActivityOverlay;
